import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';
import * as ProductsActions from '../actions/products.actions';
import { getSelectionDialogId, getProductsDialogId, getProductsTableState } from '../selectors/product.selector';
import { LaravelProductService } from '../../commons/services/laravel/laravel-product.service';
import { NewProductComponent } from '../../modules/home/modules/products/components/new-product/new-product.component';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';

@Injectable()
export class ProductEffetcs {
    error$ = createEffect(
        () =>
            this.actions$.pipe(
            ofType(
                ProductsActions.deleteProductFailed,
                ProductsActions.loadProductsFailed,
                ProductsActions.saveProductFailed,
            ),
            tap(({ error }) => {
                if (error) {
                  console.log(error);
                this.alertService.showErrorMessage('Errore', error);
                }
            })
            ),
        { dispatch: false }
    );


  loadProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActions.loadProducts),
      switchMap(({ page, perPage, order, direction, filters, includes }) => {
        return this.productService
          .list(page, perPage, order, direction, filters, includes)
          .pipe(
            map((result) =>
              ProductsActions.loadProductsCompleted({
                  product: result.data,
                  currentPage: page,
                  total: result.total,
                  perPage,
                  order,
                  direction,
                  filters,
                  includes
              })
          ),
            catchError((error) =>
              of(ProductsActions.loadProductsFailed({ error }))
            )
          );
      })
    )
  );

    changePage = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.changePage),
          withLatestFrom(this.store$.select(getProductsTableState)),
          map(
            ([
              { page, size },
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
                ProductsActions.loadProducts({
                page: page,
                perPage: size,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    changeSort = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.changeSort),
          withLatestFrom(this.store$.select(getProductsTableState)),
          map(
            ([
              action,
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
                ProductsActions.loadProducts({
                page: currentPage,
                perPage: perPage,
                order: action.order,
                direction: action.direction,
                filters,
                includes,
              })
          )
        )
    );


    changeFilters = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.changeFilters),
          withLatestFrom(this.store$.select(getProductsTableState)),
          map(
            ([
              { filters },
              { total, currentPage, perPage, direction, order, includes },
            ]) =>
                ProductsActions.loadProducts({
                page: currentPage,
                perPage: perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

  editProduct$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ProductsActions.editProduct),
        map(({ product }) => {
          let dialogRef = this.dialog.open(NewProductComponent, {
            data: {
              product,
            },
            panelClass: 'full-screen-dialog',
          });
          return ProductsActions.productDialogOpened({
            dialogId: dialogRef.id,
          });
        }),
      )
    );


    saveProduct$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.saveProduct),
          mergeMap(({ product }) =>
            this.productService.upsert(product.toDTO()).pipe(
              map((result) =>
                ProductsActions.saveProductCompleted({ product: result })
              ),
              catchError((error) =>
                of(ProductsActions.saveProductFailed({ error }))
              )
            )
          )
        )
    );

    onSaveCompleted$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.saveProductCompleted),
          map((action) => action.product),
          tap((product) =>
            this.alertService.showConfirmMessage(
              `Prodotto ${product.name} salvato con successo`
            )
          ),
          map(() => ProductsActions.closeProductsDialog())
        )
    );

    deleteProduct$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.deleteProduct),
          switchMap(({ product }) =>
            this.alertService
              .showConfirmDialog(
                'Conferma eliminazione',
                `Sei sicuro di voler eliminare l'elemento selezionato?`
              )
              .pipe(
                mergeMap((confirm) => {
                  return confirm
                    ? this.productService.delete(product.id).pipe(
                        map(() =>
                            ProductsActions.deleteProductCompleted({
                            product,
                          })
                        ),
                        catchError((error) =>
                          of(ProductsActions.deleteProductFailed({ error }))
                        )
                      )
                    : of(ProductsActions.deleteProductCancelled());
                })
              )
          )
        )
    );

    onDeleteCompleted$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.deleteProductCompleted),
          tap(({ product }) =>
            this.alertService.showConfirmMessage(
              `Prodotto ${product.name} eliminato con successo`
            )
          ),
          map(() => ProductsActions.closeProductsDialog())
        )
    );

    closeDialog = createEffect(
        () =>
          this.actions$.pipe(
            ofType(ProductsActions.closeProductsDialog),
            withLatestFrom(this.store$.select(getProductsDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
    );

    reloadAfterSave = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.saveProductCompleted),
          withLatestFrom(this.store$.select(getProductsTableState)),
          map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                ProductsActions.loadProducts({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    reloadAfterDelete = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.deleteProductCompleted),
          withLatestFrom(this.store$.select(getProductsTableState)),
          map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                ProductsActions.loadProducts({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    closeSelectionDialog = createEffect(
        () =>
          this.actions$.pipe(
            ofType(ProductsActions.closeSelectionDialog),
            withLatestFrom(this.store$.select(getSelectionDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
    );

    requestCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.requestCloseProductsDialog),
          switchMap(() => {
            const confirmDialogRef = this.dialog.open(DialogComponent, {
              disableClose: false
            });
      
            // Imposta il messaggio del dialog
            confirmDialogRef.componentInstance.title = "Chiudi";
            confirmDialogRef.componentInstance.confirmMessage =
              "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";
      
            return confirmDialogRef.afterClosed().pipe(
              map(result =>
                result
                  ? ProductsActions.confirmCloseProductsDialog()
                  : ProductsActions.cancelCloseProductsDialog()
              )
            );
          })
        )
      );

      confirmCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.confirmCloseProductsDialog),
          map(() => ProductsActions.closeProductsDialog())
        )
      );
      
      cancelCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ProductsActions.cancelCloseProductsDialog),
          map(() => {
            // Se l'utente annulla mantiene il dialog aperto
            return { type: 'NO_ACTION' };
          })
        )
      );

      closeDialog$ = createEffect(
          () =>
            this.actions$.pipe(
              ofType(ProductsActions.closeProductsDialog),
              withLatestFrom(this.store$.select(getProductsDialogId)),
              tap(([_, dialogId]) => {
                if (dialogId) {
                  this.dialog.getDialogById(dialogId).close();
                }
              })
            ),
          { dispatch: false }
        );



    constructor(
        private actions$: Actions,
        private store$: Store<AppState>,
        private dialog: MatDialog,
        private alertService: AlertService,
        private productService: LaravelProductService
    ) {}
}
